import dayjs from 'dayjs';
import { useNotifications } from 'modules/notification/hooks/useNotifications';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpertProfileStore } from 'shared/hooks/stores/useExpertProfileStore';
import { mergeClasses } from 'shared/utils/mergeClasses';
import { Notification } from 'types/Notification';
import { NotificationTypeEnum } from 'types/enums/notification-type.enum';

export type NotificationItemVariant = 'full' | 'short';

interface NotificationItemProps extends Notification {
  variant: NotificationItemVariant;
  onRedirect?: () => void;
}

const nonClickableNotificationTypes: NotificationTypeEnum[] = [
  NotificationTypeEnum.SavedOfferExpired,
];

const permanentNotificationTypes: NotificationTypeEnum[] = [
  NotificationTypeEnum.CvExtractionFailed,
  NotificationTypeEnum.AirConversationNotFinished,
  NotificationTypeEnum.JobOrderApplicationPreferencesStepNotFinished,
  NotificationTypeEnum.IncompleteProfile,
];

export const NotificationItem: FC<NotificationItemProps> = ({
  id,
  type,
  metadata,
  viewedAt,
  createdAt,
  variant = 'full',
  onRedirect,
}) => {
  const { t } = useTranslation('notification');
  const { redirectToRelatedAndMarkAsViewed } = useNotifications();
  const firstName = useExpertProfileStore((state) => state.expertProfile?.firstName);

  const { category, fullMessage, shortMessage } = t(type, {
    returnObjects: true,
    interpolation: {
      escapeValue: false,
    },
    firstName,
    ...metadata,
  }) as {
    category?: string;
    shortMessage?: string;
    fullMessage?: string;
  };
  const message = variant === 'short' ? shortMessage : fullMessage;
  const parsedCreatedAt = useMemo(() => {
    if (permanentNotificationTypes.includes(type)) {
      return null;
    }
    const daysDiff = dayjs(dayjs().startOf('day')).diff(dayjs(createdAt).startOf('day'), 'days');
    if (daysDiff < 2) {
      return t('createdAtDays', {
        count: daysDiff,
        omitCountParsing: true,
        postProcess: 'interval',
        ns: 'common',
      });
    }
    return dayjs(createdAt).format('DD.MM.YYYY');
  }, [createdAt, type, t]);

  if (!Object.values(NotificationTypeEnum).includes(type)) {
    return null;
  }

  return (
    <li key={id}>
      <button
        disabled={nonClickableNotificationTypes.includes(type)}
        className={mergeClasses(
          'h-full w-full border-b border-b-gray-300 px-6 py-4 text-left transition-all',
          !viewedAt ? 'bg-primary/5 hover:enabled:bg-primary/10' : 'hover:enabled:bg-lightBlue',
        )}
        role="link"
        onClick={() => {
          if (onRedirect) {
            onRedirect();
          }
          redirectToRelatedAndMarkAsViewed(id);
        }}
      >
        <div className="mb-2 flex justify-between">
          <p className="font-bold">{category}</p>
          {parsedCreatedAt && <p className="capitalize text-gray">{parsedCreatedAt}</p>}
        </div>
        <p>{message}</p>
      </button>
    </li>
  );
};
